/**
 * `/audiences/upload` — putting a file in, and seeing exactly what it made.
 *
 * The one thing this screen exists to prevent is an upload that quietly turns
 * into something other than what the person expected. So it does not navigate
 * away on success. It stays put and shows what was created, because **one file
 * can create several lists**: a workbook with three worksheets produces three
 * datasets, one per sheet, and they are deliberately not merged.
 *
 * That is not a technical detail, it is the correctness of the whole feature. A
 * real export carries 537 email addresses on one tab and 758 phone numbers on
 * another — different people, different lengths, no key joining them. Merging
 * them would attach one person's email to a different person's phone number and
 * then send to it. So each sheet becomes its own list, each gets its own
 * mapping, and this screen says so in as many words.
 *
 * Sheets the API skipped are listed too. An empty tab has to read as a visible
 * non-event; the alternative is a customer wondering which rows went missing.
 *
 * ## Where the sheet argument went, and what had to stay on screen
 *
 * The paragraph above used to be on the page, twice — once beside the dropzone
 * and once again on the result. It is reasoning for whoever builds the next
 * screen, so it lives here now and the screen keeps only what a reader would
 * draw a false conclusion without:
 *
 * - **"Each sheet becomes its own list."** Not optional. Somebody who uploads
 *   one file and is handed three lists has to be told that is the design and
 *   not a duplication bug, in the same breath as being handed them.
 * - **Why they are not merged** is one click away, in a disclosure, not gone.
 *   It is the answer to the only question the short version provokes, and a
 *   person who never asks it is not harmed by not reading it.
 * - **"Nothing is imported by uploading."** The whole screen's premise. A file
 *   dropped here has not reached anybody's contact list, and the person about
 *   to drop a spreadsheet of real customers is the one who needs that first.
 *   It is said twice, and the second time it is **bound to one sheet's own
 *   status** rather than sitting on the header card. The header card is
 *   rendered once and never re-rendered by an import finishing — each
 *   `SheetCard` owns its own dataset state — so a sentence up there saying
 *   nothing has been imported yet is true when the page arrives and false the
 *   moment the first of three sheets goes green, directly above a card
 *   reporting 1,240 contacts. That version was written during this slice and
 *   caught before it shipped; the fix is that the claim lives where the state
 *   that makes it true lives.
 * - **The skipped-sheet card in full.** Its subject *is* an absence — the four
 *   words that make an empty tab read as an empty tab rather than as rows that
 *   went missing are the load-bearing kind, and it only renders at all when
 *   there is something to reassure about.
 *
 * ## Slice 66: the skipped sheets are a compact warning Callout, not a card
 *
 * They were a whole `Card` with an amber border — a card-sized object for a
 * note about tabs that held nothing, sitting between the result card and the
 * sheet cards as if it were a fourth list. It is the Blueprint's compact
 * warning block now: the same title, the same "Nothing was lost" sentence,
 * the same per-sheet lines with their reasons, at the weight of a note. It
 * keeps the warn tone it had, because an unexpected empty tab is worth a
 * glance — it may be the tab somebody meant to fill — and the Callout says
 * "Warning" in words to a screen reader rather than relying on the amber. The
 * rejected alternative was `info`, which would have been calmer and would
 * also have been the only thing on the page saying a tab came up empty in
 * the same colour as "the import is running".
 *
 * The sheet labels ("Sheet 2 of 3", "sheet 4") use the shared `Kicker` role
 * rather than a hand-tuned mono string, so they match every other label.
 *
 * The disclosure is `AdvancedDisclosure` from the Integrations tabs, imported
 * rather than re-implemented. It is a `<details>`, so its contents stay in the
 * markup for find-in-page while closed. It should move to `components/`; that
 * is a shared change and this slice does not make one.
 */
import {
  ACCEPTED_UPLOAD_EXTENSIONS,
  MAX_UPLOAD_BYTES,
  MAX_UPLOAD_ROWS,
  type Dataset,
  type DatasetStatusResponse,
  type UploadDatasetResponse,
  type UploadedSheet,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dropzone,
  Kicker,
  buttonVariants,
  formatFileSize,
} from '@wend/ui';
import { ArrowLeft, ArrowRight, FileSpreadsheet, Layers, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { DatasetStatusBadge } from '../../components/DatasetStatusBadge';
import { FormAlert } from '../../components/FormAlert';
import { ImportProgress } from '../../components/ImportProgress';
import { uploadDataset } from '../../lib/api/datasets';
import { errorMessage, isApiError } from '../../lib/api/errors';
import { DATASET_STATUS_DISPLAY } from '../../lib/dataset-display';
import { formatCount, pluralise } from '../../lib/format';
import { AdvancedDisclosure } from '../integrations/AdvancedDisclosure';
import { ColumnMappingForm } from './ColumnMappingForm';

interface UploadFailure {
  message: string;
  requestId: string | undefined;
}

export function UploadPage() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<UploadFailure | null>(null);
  const [result, setResult] = useState<UploadDatasetResponse | null>(null);
  const [uploadedName, setUploadedName] = useState('');

  async function onUpload(): Promise<void> {
    if (file === null || uploading) return;

    setFailure(null);
    setUploading(true);

    try {
      const response = await uploadDataset({ file });
      setUploadedName(file.name);
      setResult(response);
      setFile(null);
    } catch (error) {
      setFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
    } finally {
      setUploading(false);
    }
  }

  function reset(): void {
    setResult(null);
    setFailure(null);
    setFile(null);
    setUploadedName('');
  }

  return (
    <AppLayout
      title="Upload a list"
      actions={
        <Link to="/audiences" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          <ArrowLeft aria-hidden="true" className="size-4" />
          All lists
        </Link>
      }
    >
      <div className="flex flex-col gap-6">
        {result === null ? (
          <Picker
            file={file}
            uploading={uploading}
            failure={failure}
            onFileChange={(next) => {
              setFile(next);
              setFailure(null);
            }}
            onUpload={() => {
              void onUpload();
            }}
          />
        ) : (
          <Result response={result} fileName={uploadedName} onUploadAnother={reset} />
        )}
      </div>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Picking a file                                                              */
/* -------------------------------------------------------------------------- */

function Picker({
  file,
  uploading,
  failure,
  onFileChange,
  onUpload,
}: {
  file: File | null;
  uploading: boolean;
  failure: UploadFailure | null;
  onFileChange: (file: File | null) => void;
  onUpload: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose a spreadsheet</CardTitle>
        <CardDescription>
          {ACCEPTED_UPLOAD_EXTENSIONS.map((extension) => extension.slice(1).toUpperCase()).join(
            ' or ',
          )}
          , up to {formatFileSize(MAX_UPLOAD_BYTES)} and {formatCount(MAX_UPLOAD_ROWS)} rows.{' '}
          <span className="text-text">Nothing is imported by uploading</span> — you map the columns
          first.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {failure !== null && (
          <FormAlert tone="error" {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}>
            {failure.message}
          </FormAlert>
        )}

        <Dropzone
          file={file}
          onFileChange={onFileChange}
          accept={ACCEPTED_UPLOAD_EXTENSIONS}
          maxSizeBytes={MAX_UPLOAD_BYTES}
          disabled={uploading}
          label="Drag your list here, or browse"
        />

        <div className="flex flex-col gap-3">
          {/*
            SLICE 29: SAID ABOUT A FILE THAT EXISTS, NOT ABOUT ONE THAT MIGHT.

            Both of these were stated speculatively, before a file was chosen —
            and then stated again with the real count once one was
            (*"X.xlsx became 3 separate lists"*, with the identical disclosure
            attached). Two disclosures, same argument, one flow.

            The post-upload pair is the one that answers the surprise, so it is
            the one that stays. This pre-answered a question nobody had yet asked,
            about a workbook that may well have one sheet.
          */}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" loading={uploading} disabled={file === null} onClick={onUpload}>
            <Upload aria-hidden="true" className="size-4" />
            {uploading ? 'Reading the file' : 'Upload and read the columns'}
          </Button>

          {/* "Choose a file to continue." was the third statement of one
              instruction in a single card: the dropzone says *Drag your list
              here, or browse*, and the button beside this said *Upload and read
              the columns* while disabled. Slice 29. */}
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* What the upload created                                                     */
/* -------------------------------------------------------------------------- */

function Result({
  response,
  fileName,
  onUploadAnother,
}: {
  response: UploadDatasetResponse;
  fileName: string;
  onUploadAnother: () => void;
}) {
  const { sheets, skippedSheets } = response;
  const multiSheet = sheets.length > 1;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {multiSheet
              ? `${fileName} became ${pluralise(sheets.length, 'separate list')}`
              : `${fileName} is ready to map`}
          </CardTitle>
          <CardDescription>
            {multiSheet ? (
              <>
                {pluralise(sheets.length, 'sheet')} with data, each its own list with its own
                mapping.
              </>
            ) : (
              <>One sheet, one list. Map its columns below to start the import.</>
            )}
          </CardDescription>

          {multiSheet && (
            <div className="mt-2">
              <AdvancedDisclosure label="Why" summary="Why these were not combined into one list">
                <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
                  Separate tabs are usually separate audiences, and joining them would attach one
                  person's email to another person's phone number. Combine them later,
                  deliberately, as a segment — the lists themselves stay as they are.
                </p>
              </AdvancedDisclosure>
            </div>
          )}
        </CardHeader>

        <CardContent className="flex flex-wrap items-center gap-3">
          <Link to="/audiences" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            All lists
          </Link>
          <Button type="button" variant="ghost" size="sm" onClick={onUploadAnother}>
            <Upload aria-hidden="true" className="size-4" />
            Upload another file
          </Button>
        </CardContent>
      </Card>

      {skippedSheets.length > 0 && <SkippedSheets skipped={skippedSheets} />}

      {sheets.map((sheet, position) => (
        <SheetCard
          key={sheet.dataset.id}
          sheet={sheet}
          position={position + 1}
          total={sheets.length}
        />
      ))}
    </>
  );
}

/**
 * The sheets that produced nothing.
 *
 * Listed rather than omitted: a workbook with a leftover blank tab must not
 * leave a person counting lists and wondering which rows disappeared.
 */
function SkippedSheets({ skipped }: { skipped: UploadDatasetResponse['skippedSheets'] }) {
  return (
    <Callout
      tone="warn"
      density="compact"
      title={`${pluralise(skipped.length, 'sheet')} held no rows to import`}
    >
      <p>
        Nothing was lost — these tabs had no data in them. Listed so an empty sheet reads as an
        empty sheet rather than as rows that went missing.
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {skipped.map((sheet) => (
          <li
            key={`${String(sheet.index)}-${sheet.name}`}
            className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5"
          >
            <span className="font-medium text-text">{sheet.name}</span>
            <Kicker as="span">sheet {sheet.index + 1}</Kicker>
            <span>{sheet.reason}</span>
          </li>
        ))}
      </ul>
    </Callout>
  );
}

/**
 * One created list: which sheet it came from, what was detected in it, and the
 * mapping form that starts its import. Each card owns its own dataset state, so
 * three sheets on this page import independently of one another.
 */
function SheetCard({
  sheet,
  position,
  total,
}: {
  sheet: UploadedSheet;
  position: number;
  total: number;
}) {
  const [dataset, setDataset] = useState<Dataset>(sheet.dataset);

  function onSettled(status: DatasetStatusResponse): void {
    setDataset((current) => ({
      ...current,
      status: status.status,
      rowCount: status.rowCount,
      importedCount: status.importedCount,
      rejectedCount: status.rejectedCount,
      error: status.error,
    }));
  }

  // With one sheet there is nothing to tell apart, so the card is simply the
  // list. With several, the worksheet is the thing that distinguishes them and
  // it leads.
  const title = total > 1 && sheet.sheetName !== null ? sheet.sheetName : dataset.name;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          {total > 1 && (
            <Kicker as="span" className="flex items-center gap-1.5">
              <FileSpreadsheet aria-hidden="true" className="size-3.5" />
              Sheet {position} of {total}
            </Kicker>
          )}
          <DatasetStatusBadge status={dataset.status} />
        </div>

        <CardTitle>{title}</CardTitle>

        <CardDescription>
          {total > 1 ? (
            <>
              Saved as <span className="text-text">{dataset.name}</span>
            </>
          ) : sheet.sheetName === null ? (
            <>From {dataset.sourceFilename}</>
          ) : (
            <>
              From {dataset.sourceFilename}, worksheet{' '}
              <span className="text-text">{sheet.sheetName}</span>
            </>
          )}{' '}
          · {pluralise(dataset.detectedColumns.length, 'column')} detected ·{' '}
          {dataset.rowCount > 0
            ? pluralise(dataset.rowCount, 'row')
            : 'rows are counted as the import runs'}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {dataset.status === 'pending' && (
          <div className="flex flex-col gap-5">
            {/* Bound to this sheet's own status rather than said once at the top
                of the page: a workbook's three cards import independently, so a
                claim about what has been imported is only true of one card at a
                time. It used to sit on the header card, where it went stale the
                moment the first sheet finished. */}
            <p className="text-[0.92rem] text-muted">
              <span className="text-text">Nothing from this sheet has been imported yet.</span>{' '}
              Confirming the mapping starts the import.
            </p>
            <ColumnMappingForm
              key={dataset.id}
              dataset={dataset}
              suggestedMapping={sheet.suggestedMapping}
              onImportStarted={setDataset}
            />
          </div>
        )}

        {dataset.status === 'processing' && (
          <ImportProgress initial={dataset} onSettled={onSettled} />
        )}

        {dataset.status === 'ready' && (
          <div className="flex flex-col gap-4">
            <p className="text-[0.92rem] leading-relaxed text-muted">
              {formatCount(dataset.importedCount)} of {formatCount(dataset.rowCount)} rows became
              contacts
              {dataset.rejectedCount === 0
                ? ' and nothing was rejected.'
                : `, and ${pluralise(dataset.rejectedCount, 'row')} could not be imported — the list has the reason for each.`}
            </p>
            <div>
              <Link
                to={`/audiences/${dataset.id}`}
                className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              >
                Open this list
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            </div>
          </div>
        )}

        {dataset.status === 'failed' && (
          <div className="flex flex-col gap-4">
            <FormAlert tone="error">
              {dataset.error ?? DATASET_STATUS_DISPLAY.failed.description}
            </FormAlert>
            <div>
              <Link
                to={`/audiences/${dataset.id}`}
                className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              >
                Open this list to try again
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
